import React, { useState, useEffect, useRef } from 'react';
import IntroductionAudio from '../IntroductionAudio';

const ArcQuizPage = ({ page, onNext, onCompletionChange }) => {
  const [selectedVowel, setSelectedVowel] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const timerRef = useRef(null);
  const questionAudioRef = useRef(null);

  const questions = page.questions || [];
  const vowels = page.vowels || [
    { id: 'a', displayText: 'a' },
    { id: 'e', displayText: 'e' },
    { id: 'i', displayText: 'i' },
    { id: 'o', displayText: 'o' },
    { id: 'u', displayText: 'u' }
  ];

  const currentQuestion = questions[currentQuestionIndex];
  const timeLimit = page.timeLimit || 5;

  useEffect(() => {
    if (isStarted && currentQuestion && currentQuestionIndex < questions.length) {
      if (timerRef.current) clearInterval(timerRef.current);
      
      setSelectedVowel(null);
      setTimeRemaining(null);
      setIsPlaying(false);

      if (currentQuestion.audio && questionAudioRef.current) {
        questionAudioRef.current.src = currentQuestion.audio.url;
        questionAudioRef.current.play().catch(err => {

          startTimer();
        });
        setIsPlaying(true);
        
        questionAudioRef.current.onended = () => {
          setIsPlaying(false);
          startTimer();
        };
      } else {
        startTimer();
      }
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isStarted, currentQuestionIndex, page]);

  const startTimer = () => {
    setTimeRemaining(timeLimit);
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStart = () => {
    setIsStarted(true);
    setCurrentQuestionIndex(0);
    setSelectedVowel(null);
    setAnswers([]);
  };

  const handleTimeout = () => {
    const answer = {
      questionIndex: currentQuestionIndex,
      selectedVowel: null,
      correctVowel: currentQuestion.correctVowel,
      isCorrect: false,
      timedOut: true
    };
    setAnswers([...answers, answer]);

    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => setCurrentQuestionIndex(currentQuestionIndex + 1), 1000);
    } else {
      finishQuiz([...answers, answer]);
    }
  };

  const finishQuiz = (finalAnswers) => {
    setIsStarted(false);
    setCurrentQuestionIndex(0);
    setIsCompleted(true);
    setShowResults(true);
    if (onCompletionChange) onCompletionChange(true);
  };

  const moveToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => setCurrentQuestionIndex(currentQuestionIndex + 1), 1000);
    } else {
      finishQuiz(answers);
    }
  };

  const handleRetry = () => {
    setIsStarted(false);
    setCurrentQuestionIndex(0);
    setSelectedVowel(null);
    setAnswers([]);
    setIsCompleted(false);
    setShowResults(false);
    if (onCompletionChange) onCompletionChange(false);
  };

  const handleVowelClick = (vowel) => {
    if (!isStarted || isPlaying || selectedVowel || timeRemaining === 0) return;

    setSelectedVowel(vowel.id);
    if (timerRef.current) clearInterval(timerRef.current);

    const isCorrect = vowel.id === currentQuestion.correctVowel;
    const answer = {
      questionIndex: currentQuestionIndex,
      selectedVowel: vowel.id,
      correctVowel: currentQuestion.correctVowel,
      isCorrect,
      timedOut: false
    };
    setAnswers([...answers, answer]);

    moveToNextQuestion();
  };

  const getVowelStyle = (vowel) => {
    const isSelected = selectedVowel === vowel.id;
    if (isSelected) {
      return { transform: 'translate(-50%, -50%) scale(1.3)', zIndex: 20 };
    }
    return {
      cursor: (!isStarted || isPlaying || selectedVowel || timeRemaining === 0) ? 'not-allowed' : 'pointer',
      opacity: (!isStarted || isPlaying || selectedVowel || timeRemaining === 0) ? 0.5 : 1
    };
  };

  
  
  const VIEWBOX_WIDTH = 400;
  const VIEWBOX_HEIGHT = 280;
  const VIEWBOX_MIN_X = 0;
  const VIEWBOX_MIN_Y = -80;

  
  const CENTER_X = 200;
  const CENTER_Y = 170;
  
  const RADIUS = 130; 
  const EFFECTIVE_RADIUS = RADIUS; 

  const VOWEL_ORDER = ['u', 'o', 'i', 'e', 'a']; 
  const NUM_VOWELS = VOWEL_ORDER.length;

  const getVowelPosition = (vowelId) => {
    const index = VOWEL_ORDER.indexOf(vowelId);
    if (index === -1) return { left: '50%', top: '50%' };

    
    const startAngleDeg = 165; 
    const endAngleDeg = 15;
    
    const angleDeg = startAngleDeg + (index / (NUM_VOWELS - 1)) * (endAngleDeg - startAngleDeg);
    const angleRad = Math.PI * angleDeg / 180;

    const svgX = CENTER_X + EFFECTIVE_RADIUS * Math.cos(angleRad);
    const svgY = CENTER_Y - EFFECTIVE_RADIUS * Math.sin(angleRad); 

    const percentX = ((svgX - VIEWBOX_MIN_X) / VIEWBOX_WIDTH) * 100;
    const percentY = ((svgY - VIEWBOX_MIN_Y) / VIEWBOX_HEIGHT) * 100;

    return {
      position: 'absolute',
      left: `${percentX}%`,
      top: `${percentY}%`,
      transform: 'translate(-50%, -50%)'
    };
  };

  const correctCount = answers.filter(a => a.isCorrect).length;
  const totalQuestions = questions.length;
  const scorePercentage = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const passingScore = page.passingScore || 80;
  const canProceed = scorePercentage >= passingScore;

  return (
    <div>
      {page.introductionAudio && !isStarted && !showResults && (
        <div className="mb-6">
          <IntroductionAudio audio={page.introductionAudio} autoPlay={page.introductionAudio.autoPlay} />
        </div>
      )}

      {page.title && <h2 className="text-2xl font-bold mb-4 text-gray-800">{page.title}</h2>}

      {page.instructions && !showResults && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-gray-700">{page.instructions.text}</p>
        </div>
      )}

      {!isStarted && !showResults && (
        <div className="mb-6 text-center">
          <button
            onClick={handleStart}
            className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
          >
            {questions.length > 0 ? 'Start Quiz' : 'Begin'}
          </button>
        </div>
      )}

      {isStarted && questions.length > 0 && (
        <div className="mb-4 text-center">
          <span className="text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
        </div>
      )}

      {isStarted && timeRemaining !== null && (
        <div className="mb-4 text-center">
          <span className={`text-2xl font-bold ${timeRemaining <= 2 ? 'text-red-600' : 'text-gray-700'}`}>
            {timeRemaining}s
          </span>
        </div>
      )}

      
      <div className="mb-6 mx-auto w-full max-w-lg">
        <div className="relative w-full" style={{ aspectRatio: '400 / 280' }}>
          
          <svg
            className="absolute inset-0 w-full h-full block"
            viewBox="0 -80 400 280"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M 70 150 A 130 130 0 0 1 330 150"
              stroke="#654321"
              strokeWidth="60"
              fill="none"
              strokeLinecap="round"
              transform="translate(0, 20)"
              style={{ filter: 'drop-shadow(4px 4px 8px rgba(0,0,0,0.5))' }}
            />
          </svg>

          {/* Vowels */}
          {vowels.map((vowel) => {
            const position = getVowelPosition(vowel.id);
            const style = getVowelStyle(vowel);

            return (
              <button
                key={vowel.id}
                onClick={() => handleVowelClick(vowel)}
                disabled={!isStarted || isPlaying || selectedVowel || timeRemaining === 0}
                className="absolute flex items-center justify-center font-bold transition-all duration-300"
                style={{
                  ...position,
                  ...style,
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  textShadow: '3px 3px 8px rgba(0,0,0,1)',
                  fontSize: 'clamp(2rem, 8vw, 4rem)',
                  zIndex: 100,
                  pointerEvents: 'auto',
                  padding: 0, 
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  if (selectedVowel !== vowel.id && isStarted && !isPlaying && timeRemaining > 0) {
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedVowel !== vowel.id) {
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                  }
                }}
              >
                {vowel.displayText}
              </button>
            );
          })}
        </div>
      </div>

      <audio ref={questionAudioRef} preload="metadata" />

      {showResults && answers.length > 0 && (
        <div className="mt-8 text-center">
          <div className="text-6xl font-bold mb-6" style={{ color: canProceed ? '#10b981' : '#ef4444' }}>
            {scorePercentage}%
          </div>
          
          {scorePercentage === 100 ? (
            <button onClick={onNext} className="px-8 py-4 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg">
              Excellent
            </button>
          ) : scorePercentage >= 80 ? (
            <button onClick={onNext} className="px-8 py-4 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg">
              Good
            </button>
          ) : (
            <button onClick={handleRetry} className="px-8 py-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-lg">
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ArcQuizPage;